// Data access layer for Events
import { PostgrestError } from '@supabase/supabase-js';
import { getSupabaseClient } from '../lib/supabase';

export type Event = {
  id: string;
  user_id: string | null;
  event_name: string;
  metadata: Record<string, any>;
  timestamp: string;
  created_at: string;
  // Joined from profiles
  user_email?: string;
  user_name?: string;
};

export type ListParams = {
  /** Free-text search against event name, user email, or user name */
  q?: string;
  /** Filter by event name */
  event_name?: string;
  /** Filter by user ID */
  user_id?: string;
  /** Date range filters */
  start_date?: string;
  end_date?: string;
  /** Pagination */
  page?: number;
  pageSize?: number;
};

export type ListResult = {
  items: Event[];
  total: number;
};

type SingleResponse<T> = { data: T | null; error: PostgrestError | null };
type ListResponse<T> = { data: T[] | null; error: PostgrestError | null; count?: number | null };

function isNotFound(error: PostgrestError | null): boolean {
  return !!error && error.code === 'PGRST116';
}

function unwrapSingle<T>(response: SingleResponse<T>): T | null {
  if (response.error) {
    if (isNotFound(response.error)) return null;
    throw response.error;
  }
  return response.data ?? null;
}

function unwrapList<T>(response: ListResponse<T>): T[] {
  if (response.error) {
    if (isNotFound(response.error)) return [];
    throw response.error;
  }
  return response.data ?? [];
}

/**
 * List events with filters and pagination
 */
export async function listEvents(params: ListParams = {}): Promise<ListResult> {
  const {
    q = '',
    event_name,
    user_id,
    start_date,
    end_date,
    page = 1,
    pageSize = 15,
  } = params;

  const supabase = getSupabaseClient();

  // Build the query for events
  let query = supabase
    .from('events')
    .select('*', { count: 'exact' });

  // Apply filters
  if (event_name) {
    query = query.eq('event_name', event_name);
  }

  if (user_id) {
    query = query.eq('user_id', user_id);
  }

  if (start_date) {
    query = query.gte('timestamp', start_date);
  }

  if (end_date) {
    query = query.lte('timestamp', end_date);
  }

  // Order by timestamp descending (most recent first)
  query = query.order('timestamp', { ascending: false });

  // Apply pagination
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  query = query.range(from, to);

  const response = await query;

  if (response.error) {
    throw response.error;
  }

  const eventsData = response.data ?? [];

  // Get unique user IDs (filter out null values)
  const userIds = [...new Set(eventsData.map((e: any) => e.user_id).filter((id: any) => id !== null))];

  // Fetch profiles for these user IDs
  const profilesMap = new Map<string, { email: string; full_name: string }>();

  if (userIds.length > 0) {
    const { data: profilesData } = await supabase
      .from('profiles')
      .select('id, email, full_name')
      .in('id', userIds);

    if (profilesData) {
      profilesData.forEach((profile: any) => {
        profilesMap.set(profile.id, {
          email: profile.email,
          full_name: profile.full_name,
        });
      });
    }
  }

  // Transform the response to include profile data
  const events: Event[] = eventsData.map((row: any) => {
    const profile = row.user_id ? profilesMap.get(row.user_id) : null;
    return {
      id: row.id,
      user_id: row.user_id,
      event_name: row.event_name,
      metadata: row.metadata ?? {},
      timestamp: row.timestamp,
      created_at: row.created_at,
      user_email: profile?.email ?? null,
      user_name: profile?.full_name ?? null,
    };
  });

  // Client-side search filter if q is provided
  let filteredEvents = events;
  if (q) {
    const lowerQ = q.toLowerCase();
    filteredEvents = events.filter(
      (event) =>
        event.event_name.toLowerCase().includes(lowerQ) ||
        event.user_email?.toLowerCase().includes(lowerQ) ||
        event.user_name?.toLowerCase().includes(lowerQ) ||
        event.id.toLowerCase().includes(lowerQ)
    );
  }

  return {
    items: filteredEvents,
    total: response.count ?? filteredEvents.length,
  };
}

/**
 * Get a single event by ID
 */
export async function getEvent(id: string): Promise<Event | null> {
  const supabase = getSupabaseClient();

  const response = await supabase
    .from('events')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  const row = unwrapSingle(response);
  if (!row) return null;

  // Fetch the profile separately (only if user_id is not null)
  let profile = null;
  if (row.user_id) {
    const { data: profileData } = await supabase
      .from('profiles')
      .select('email, full_name')
      .eq('id', row.user_id)
      .maybeSingle();
    profile = profileData;
  }

  return {
    id: row.id,
    user_id: row.user_id,
    event_name: row.event_name,
    metadata: row.metadata ?? {},
    timestamp: row.timestamp,
    created_at: row.created_at,
    user_email: profile?.email ?? null,
    user_name: profile?.full_name ?? null,
  };
}

/**
 * Get unique event names for filtering
 */
export async function getEventNames(): Promise<string[]> {
  const supabase = getSupabaseClient();

  const response = await supabase
    .from('events')
    .select('event_name')
    .order('event_name');

  const rows = unwrapList(response);
  const uniqueNames = [...new Set(rows.map((r: any) => r.event_name))];
  return uniqueNames.sort();
}

/**
 * Get alerts (session_no_show, seeker_rejected_match, guide_rejected_match, seeker_not_matched events)
 */
export async function getAlerts(params: {
  event_type?: 'session_no_show' | 'seeker_rejected_match' | 'guide_rejected_match' | 'seeker_not_matched' | 'all';
  page?: number;
  pageSize?: number;
} = {}): Promise<ListResult> {
  const { event_type = 'all', page = 1, pageSize = 20 } = params;

  const supabase = getSupabaseClient();

  const alertEventNames = ['session_no_show', 'seeker_rejected_match', 'guide_rejected_match', 'seeker_not_matched'];

  let query = supabase
    .from('events')
    .select('*', { count: 'exact' });

  // Filter by specific alert type or all alert types
  if (event_type !== 'all') {
    query = query.eq('event_name', event_type);
  } else {
    query = query.in('event_name', alertEventNames);
  }

  // Order by timestamp descending (most recent first)
  query = query.order('timestamp', { ascending: false });

  // Apply pagination
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  query = query.range(from, to);

  const response = await query;

  if (response.error) {
    throw response.error;
  }

  const eventsData = response.data ?? [];

  // Get unique user IDs (filter out null values)
  const userIds = [...new Set(eventsData.map((e: any) => e.user_id).filter((id: any) => id !== null))];

  // Fetch profiles for these user IDs
  const profilesMap = new Map<string, { email: string; full_name: string }>();

  if (userIds.length > 0) {
    const { data: profilesData } = await supabase
      .from('profiles')
      .select('id, email, full_name')
      .in('id', userIds);

    if (profilesData) {
      profilesData.forEach((profile: any) => {
        profilesMap.set(profile.id, {
          email: profile.email,
          full_name: profile.full_name,
        });
      });
    }
  }

  // Transform the response to include profile data
  const events: Event[] = eventsData.map((row: any) => {
    const profile = row.user_id ? profilesMap.get(row.user_id) : null;
    return {
      id: row.id,
      user_id: row.user_id,
      event_name: row.event_name,
      metadata: row.metadata ?? {},
      timestamp: row.timestamp,
      created_at: row.created_at,
      user_email: profile?.email ?? null,
      user_name: profile?.full_name ?? null,
    };
  });

  return {
    items: events,
    total: response.count ?? events.length,
  };
}
